import type {
  ActionGroupStatus,
  Job,
  LoopRecordListActionGroupConfig,
  OutputData,
  OutputReference,
  RecordJobData,
  RecordListInputConfig,
  RecordOutputData,
  ValueData,
} from "@smartsuite/automation-engine-api";
import { RecordListInputConfig_Reference } from "@smartsuite/automation-engine-api";
import {
  valueDataToString,
  valueDataVToString,
} from "@smartsuite/automation-conditions";
import {
  findActionsGroupInstanceId,
  getRecordJobId,
  resolveOutputReference,
} from "../../../shared/status-walk";
import { findRecordValue } from "../../../ActivityGrid/ActivityGrid.helpers";
import { findConfigGroupByInstanceId } from "../../../shared/config-walk";

/** One `key: value` pair of a structured (webhook object) Current Item.
 * `kind` mirrors the stored ValueData type so the code-block view can render
 * and copy numbers unquoted. */
export interface CurrentItemPair {
  key: string;
  value: string;
  kind: "string" | "number";
}

export interface CurrentItemView {
  caption: string;
  value: string;
  /** Present only for a keyValue (object-shaped) array item -- the box then
   * renders the code-block view instead of the plain value line. */
  pairs?: CurrentItemPair[];
}

const RECORD_TITLE_OUTPUT_ID = "title";

/**
 * Reads a record's title (output "title") into the Current Item view. Uses the
 * canonical `findRecordValue` (the same extractor the rest of run history uses
 * for record names), which is null-safe end to end: a missing record, missing
 * output, or non-string value all yield "" -> undefined here, so no value ever
 * throws in this render path.
 */
const recordTitleView = (
  record: RecordOutputData | undefined,
): CurrentItemView | undefined => {
  const value = findRecordValue(RECORD_TITLE_OUTPUT_ID, record);
  return value.trim() === ""
    ? undefined
    : { caption: "job-list.record-title", value };
};

/**
 * Extracts the current record from a lazily-fetched RecordJobData and renders
 * its title. A find-records result list is NOT carried inline in the job
 * status (the action status stores only `recordListCount`); instead the action
 * produces a SINGLE record-list job (record-0) holding ALL found records under
 * `outputData.recordList.recordList.records` (verified against a real dev
 * payload -- there is no per-record job, record-1+ 404). The loop iteration's
 * record is indexed out of that list by `recordIndex`; a `record`-shaped job
 * (single record) is normalised to a one-element list.
 */
const recordTitleFromRecordJob = (
  recordJob: RecordJobData | undefined,
  recordIndex: number,
): CurrentItemView | undefined => {
  if (recordJob === undefined) {
    return undefined;
  }
  const records =
    recordJob.outputData?.recordList?.recordList !== undefined
      ? recordJob.outputData.recordList.recordList.records
      : recordJob.record !== undefined
        ? [recordJob.record]
        : [];
  return recordTitleView(records[recordIndex]);
};

/**
 * An "array of objects" item (webhook request-content) is a ValueData whose
 * entries are ALL keyValue pairs -- structured in storage (hotfix job
 * a8c9a2b9), but valueDataToString flattens them into one unreadable line
 * ("[text]: v50[textarea]: ..."). Extracts the structured pairs instead, so
 * CurrentItemBox can render its code-block view (and `value` keeps a plain
 * one-pair-per-line fallback string). Returns undefined for any other shape,
 * and the caller falls back to valueDataToString.
 */
const keyValueItemPairs = (
  item: ValueData,
  timezone: string | undefined,
): CurrentItemPair[] | undefined => {
  if (item.values.length === 0) {
    return undefined;
  }
  const rawPairs = item.values.map((entry) =>
    entry.value?.oneofKind === "keyValue" ? entry.value.keyValue : undefined,
  );
  if (rawPairs.some((pair) => pair === undefined)) {
    return undefined;
  }
  return rawPairs.map((pair) => ({
    key: pair?.key === undefined ? "" : valueDataToString(pair.key, timezone),
    value:
      pair?.value === undefined ? "" : valueDataToString(pair.value, timezone),
    kind:
      pair?.value?.values[0]?.value.oneofKind === "number"
        ? ("number" as const)
        : ("string" as const),
  }));
};

/**
 * Webhook/AI array source: the output resolves to a single ValueData.V of
 * oneofKind "list" (ListData.values: ValueData[]), each entry a full
 * record/object -- index into it directly rather than flattening via
 * valueDataVToString's own "list" case (which joins every entry).
 */
const fromArraySource = (
  value: ValueData | undefined,
  timezone: string | undefined,
  recordIndex: number,
): CurrentItemView | undefined => {
  const first = value?.values[0];
  if (first?.value?.oneofKind !== "list") {
    return undefined;
  }

  const item = first.value.list.values[recordIndex];
  if (item === undefined) {
    return undefined;
  }

  try {
    const pairs = keyValueItemPairs(item, timezone);
    if (pairs !== undefined) {
      const lines = pairs.map((pair) => `${pair.key}: ${pair.value}`);
      return {
        caption: "job-list.array-item",
        value: `\n${lines.join("\n")}`,
        pairs,
      };
    }
    const stringValue = valueDataToString(item, timezone);
    return stringValue.trim() === ""
      ? undefined
      : { caption: "job-list.array-item", value: stringValue };
  } catch {
    return undefined;
  }
};

/**
 * Multiple-value field source: the output resolves to a ValueData whose
 * `values` array itself carries one V per selected value (e.g. a multi-select
 * / linked-record field) -- index directly into `values[recordIndex]`.
 */
const fromFieldSource = (
  value: ValueData | undefined,
  timezone: string | undefined,
  recordIndex: number,
): CurrentItemView | undefined => {
  const item = value?.values[recordIndex];
  if (item === undefined) {
    return undefined;
  }

  try {
    const stringValue = valueDataVToString(item, timezone);
    return stringValue.trim() === ""
      ? undefined
      : { caption: "job-list.field-value", value: stringValue };
  } catch {
    return undefined;
  }
};

/**
 * The loop's source discriminator from its config snapshot, or undefined when
 * the status group isn't a loop or has no matching config (found via
 * findConfigGroupByInstanceId on the loop's own instanceId).
 */
const getLoopSource = (
  loopStatusGroup: ActionGroupStatus,
  job: Job,
): LoopRecordListActionGroupConfig["source"] | undefined => {
  if (loopStatusGroup.status.oneofKind !== "loop") {
    return undefined;
  }
  const config = findConfigGroupByInstanceId(
    job.automation?.actionGroups ?? [],
    loopStatusGroup.instanceId,
  );
  return config?.config.oneofKind === "loop"
    ? config.config.loop.source
    : undefined;
};

/**
 * recordJobId to lazily fetch the source records of a find-action-sourced
 * loop, or undefined when the source is not an action-referenced record list
 * (trigger lists resolve inline; output references have no record job). A
 * find/record-list action produces a SINGLE record-list job at record index 0
 * holding ALL found records (there is no per-iteration record-N job), so every
 * iteration resolves against the same record-0 job and indexes into its list.
 */
const recordJobIdForAction = (
  actionInstanceId: number,
  job: Job,
): string | undefined => {
  const groupInstanceId = findActionsGroupInstanceId(
    job.actionGroupsStatus,
    actionInstanceId,
  );
  if (groupInstanceId === undefined) {
    return undefined;
  }
  return getRecordJobId(job.jobId, groupInstanceId, actionInstanceId, 0);
};

const recordJobIdForActionSource = (
  recordListConfig: RecordListInputConfig,
  job: Job,
): string | undefined => {
  const { recordList } = recordListConfig;
  if (recordList.oneofKind !== "actionReference") {
    return undefined;
  }
  return recordJobIdForAction(recordList.actionReference.instanceId, job);
};

/**
 * The value of an ACTION-referenced output, read off that action's lazily
 * fetched record job. An action's own outputs are not inline in the job status
 * -- the status carries only `recordListCount` -- so a loop sourced from an
 * action resolves nothing from `actionGroupsStatus`, and the record job is
 * searched in the two places an action can put an output:
 *
 *  - a RECORD action hangs it off its record, under
 *    `outputData.recordList.recordList.records[0].outputs` (hotfix job
 *    9d97f9d9: a Create Record action with `outputs: []`, no `recordList`, and
 *    the Email field carrying both values on its record job). A `record`-shaped
 *    job is normalised to a one-element list, matching recordTitleFromRecordJob;
 *  - a NON-record action (Send Webhook, AI) has no such record and keeps the
 *    output at action level, in `outputData.outputs` -- the same place the
 *    webhook run-history row reads it from (generate-webhook-group.hook.ts).
 *    Hotfix job eaa8c35d, a loop over a send-webhook action's
 *    `response-content.json`: the record job at group-0/action-1/record-0 holds
 *    the response there, while its `record` is the unrelated TRIGGER record
 *    (searching only the record shape found no match, so the Current Item box
 *    never appeared -- STR-14493).
 *
 * Undefined until the fetch resolves, so the box appears once it arrives.
 */
const actionOutputValueFromRecordJob = (
  reference: OutputReference,
  job: Job,
  recordJobData: Record<string, RecordJobData> | undefined,
): ValueData | undefined => {
  if (
    reference.source.oneofKind !== "actionReference" ||
    recordJobData === undefined
  ) {
    return undefined;
  }
  const recordJobId = recordJobIdForAction(
    reference.source.actionReference.instanceId,
    job,
  );
  if (recordJobId === undefined) {
    return undefined;
  }
  const recordJob = recordJobData[recordJobId];
  const records =
    recordJob?.outputData?.recordList?.recordList !== undefined
      ? recordJob.outputData.recordList.recordList.records
      : recordJob?.record !== undefined
        ? [recordJob.record]
        : [];
  const findOutput = (outputs: OutputData[]): ValueData | undefined =>
    outputs.find((output) => output.outputId === reference.outputId)?.value;

  // Record shape first: it is the verified resolution for a record action, and
  // it yields undefined (never a wrong value) for a non-record one.
  return (
    findOutput(records[0]?.outputs ?? []) ??
    findOutput(recordJob?.outputData?.outputs ?? [])
  );
};

/**
 * When the loop's Current Item needs a lazily-fetched RecordJobData (a
 * find-action-sourced record list), returns the recordJobId the caller must
 * fetch before getCurrentItemView can resolve it; undefined for every source
 * that resolves synchronously (trigger record list, output references).
 */
export const getCurrentItemRecordJobId = (
  loopStatusGroup: ActionGroupStatus,
  job: Job,
): string | undefined => {
  const source = getLoopSource(loopStatusGroup, job);
  if (source?.oneofKind === "recordList") {
    return recordJobIdForActionSource(source.recordList, job);
  }
  // An ACTION-referenced output needs that action's record job too (see
  // actionOutputValueFromRecordJob); a trigger-referenced one resolves inline.
  if (source?.oneofKind === "outputReference") {
    const { source: referenceSource } = source.outputReference;
    return referenceSource.oneofKind === "actionReference"
      ? recordJobIdForAction(referenceSource.actionReference.instanceId, job)
      : undefined;
  }
  return undefined;
};

/**
 * True when this iteration's Current Item should come from GetLoopItemData
 * (api 7.25.0): the status stamps a real loopItemId ("" marks a pre-7.25 job
 * -> legacy resolution) and the loop is record-list-sourced. outputReference
 * sources (webhook/AI array, multi-value field) keep their inline resolution:
 * their captions ("Array item" / "Field value") aren't derivable from the
 * engine outputs alone.
 */
export const currentItemUsesLoopItemData = (
  loopStatusGroup: ActionGroupStatus,
  job: Job,
  loopItemId: string,
): boolean =>
  loopItemId !== "" &&
  getLoopSource(loopStatusGroup, job)?.oneofKind === "recordList";

/**
 * Current Item view from GetLoopItemData's outputs: the engine resolves the
 * iteration's record server-side, so the record's `title` output is read
 * directly -- no record-0 indexing. Missing/empty/malformed values degrade to
 * undefined (box hidden), same contract as the legacy strategies.
 */
export const currentItemFromLoopOutputs = (
  outputs: OutputData[] | undefined,
  timezone: string | undefined,
): CurrentItemView | undefined => {
  const title = outputs?.find(
    (output) => output.outputId === RECORD_TITLE_OUTPUT_ID,
  );
  if (title?.value === undefined) {
    return undefined;
  }
  try {
    const value = valueDataToString(title.value, timezone);
    return value.trim() === ""
      ? undefined
      : { caption: "job-list.record-title", value };
  } catch {
    return undefined;
  }
};

/**
 * Current Item resolver (spec 13.13). `loopStatusGroup` identifies the loop
 * via its instanceId; its matching config carries the `source` discriminator
 * that picks the strategy:
 *  - record list FROM_TRIGGER: resolved inline from `job.trigger.recordList`;
 *  - record list from a find action: the results are NOT inline in the job
 *    status, so the current record is read from `recordJobData` (fetched via
 *    the recordJobId from getCurrentItemRecordJobId) -- undefined until it
 *    arrives, so the box appears once the fetch resolves;
 *  - output reference (webhook/AI array or multi-value field): resolved inline
 *    from the referenced output.
 * Anything unresolvable returns undefined so the caller hides the box rather
 * than showing garbage.
 */
export const getCurrentItemView = (
  loopStatusGroup: ActionGroupStatus,
  job: Job,
  recordIndex: number,
  recordJobData?: Record<string, RecordJobData>,
): CurrentItemView | undefined => {
  const source = getLoopSource(loopStatusGroup, job);
  if (source === undefined) {
    return undefined;
  }

  if (source.oneofKind === "recordList") {
    const { recordList } = source.recordList;
    if (recordList.oneofKind === "reference") {
      // Trigger-sourced lists ARE inline in the job payload.
      if (
        recordList.reference !== RecordListInputConfig_Reference.FROM_TRIGGER
      ) {
        return undefined;
      }
      return recordTitleView(
        job.trigger?.recordList?.recordList?.records[recordIndex],
      );
    }
    // Find-action-sourced lists are fetched lazily: one record-0 job holds all
    // records, and this iteration's record is indexed out of it by recordIndex.
    const recordJobId = recordJobIdForActionSource(source.recordList, job);
    if (recordJobId === undefined || recordJobData === undefined) {
      return undefined;
    }
    return recordTitleFromRecordJob(recordJobData[recordJobId], recordIndex);
  }

  if (source.oneofKind === "outputReference") {
    // Resolve the referenced output once and hand the same ValueData to both
    // strategies. "list"-shaped values (webhook/AI arrays) are checked first:
    // a single-V ValueData whose only entry happens to be a "list" would
    // otherwise be misread as a one-item field source at recordIndex 0.
    //
    // Inline first: a non-record action (webhook / AI) keeps its output in the
    // status' own `outputs`. A RECORD action leaves that empty and carries the
    // field on its record job instead, so fall back to the lazy fetch.
    const reference = source.outputReference;
    const value =
      resolveOutputReference(reference, job)?.value ??
      actionOutputValueFromRecordJob(reference, job, recordJobData);
    const timezone = job.automation?.timezone;
    return (
      fromArraySource(value, timezone, recordIndex) ??
      fromFieldSource(value, timezone, recordIndex)
    );
  }
  return undefined;
};
